import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import api, {resolveImage} from "../lib/api";
import {useSettings} from "../context/SettingsContext";
import Seo from "../components/site/Seo";
import {ArrowRight, Sparkles, Rocket, ShieldCheck, Layers, Globe, Zap, Star} from "lucide-react";

const productAbstracts = {
    "reptile-finance": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/4f325e480eaf6cd11c4f52e6a9ef89612848c6bce8c71051ec8c3d00c4c24b7c.png",
    "reptile-udharibook": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/4f325e480eaf6cd11c4f52e6a9ef89612848c6bce8c71051ec8c3d00c4c24b7c.png",
    "tracko": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/9f6457beb8abc717b85dacb7d97f11b602ce1e9130e9cdd759ac69cd29f495e9.png",
    "emi-security-plus": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/9f6457beb8abc717b85dacb7d97f11b602ce1e9130e9cdd759ac69cd29f495e9.png",
    "servexo": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/9f6457beb8abc717b85dacb7d97f11b602ce1e9130e9cdd759ac69cd29f495e9.png",
    "grabnchat": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/babb99c37925bbc9fe93bffc81ba5c0fd009b4c07fb1ecfa06d9dd6aa7170c5f.png",
    "reptile-it-service": "https://static.prod-images.emergentagent.com/jobs/dfd40c82-155c-446f-b6a2-3614c96b8638/images/babb99c37925bbc9fe93bffc81ba5c0fd009b4c07fb1ecfa06d9dd6aa7170c5f.png",
};

const ProductCard = ({p, span = "md:col-span-4", accent = "orange"}) => {
    // const img = productAbstracts[p.slug];
    const img = p.hero_image;
    const accentBar = accent === "green" ? "bg-green-500" : "bg-orange-500";
    const accentText = accent === "green" ? "text-green-700" : "text-orange-700";
    const accentBg = accent === "green" ? "bg-green-50" : "bg-orange-50";
    return (
        <Link
            to={`/products/${p.slug}`}
            data-testid={`home-product-${p.slug}`}
            className={`group ${span} bg-white border border-zinc-200 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col`}
        >
            <div className="flex items-center justify-between mb-5">
        <span
            className={`text-xs uppercase tracking-[0.18em] font-bold ${accentText} ${accentBg} px-3 py-1 rounded-full`}>
          {p.category || "Product"}
        </span>
                <ArrowRight className="text-zinc-400 group-hover:text-orange-500 group-hover:translate-x-1 transition"
                            size={18}/>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-zinc-900 mb-2">{p.name}</h3>
            <p
                className="text-zinc-600 text-sm leading-relaxed mb-6 flex-1 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: p.short_description }}
            />
            {img && (
                <div className="rounded-xl overflow-hidden border border-zinc-100 aspect-[16/9]">
                    <img  src={`${process.env.REACT_APP_BACKEND_URL}${p.hero_image}`} alt={p.name} className="w-full h-full object-cover"/>
                </div>
            )}
            <div className={`h-1 ${accentBar} rounded-full mt-6 w-12 group-hover:w-24 transition-all`}/>
        </Link>
    );
};

const Home = () => {
    const [products, setProducts] = useState([]);
    const {settings} = useSettings();

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {
        });
    }, []);

    const seo = settings?.home_seo || {};

    return (
        <div data-testid="home-page">
            <Seo
                title={seo.title || "Reptile Global — Software Products & IT Services"}
                description={seo.description}
                keywords={seo.keywords}
                ogImage={seo.og_image}
                ogType={seo.og_type || "website"}
                ogUrl={seo.og_url}
                twitterCard={seo.twitter_card}
                canonicalUrl={seo.canonical_url}
                jsonLd={seo.json_ld}
                organizationSchema={settings?.organization_schema}
            />

            {/* Hero */}
            <section className="relative overflow-hidden bg-grid">
                <div
                    className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-orange-200/40 rounded-full blur-3xl pointer-events-none"/>
                <div
                    className="absolute -bottom-32 -left-32 w-[420px] h-[420px] bg-green-200/40 rounded-full blur-3xl pointer-events-none"/>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 fade-in-up">
                            <div
                                className="inline-flex items-center gap-2 bg-white border border-zinc-200 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide text-zinc-700 mb-8 shadow-sm">
                                <Sparkles size={14} className="text-orange-500"/>
                                Building flagship software since day one
                            </div>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-zinc-950 leading-[0.95]">
                                Software that <span className="text-orange-500">scales</span><br/>
                                businesses across <span className="text-green-600">the globe</span>.
                            </h1>
                            <p className="mt-8 text-lg text-zinc-600 max-w-xl leading-relaxed">
                                {settings?.tagline || settings?.description || "We design, build and ship powerful SaaS products and custom software for finance, HR, retail and customer engagement."}
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link to="/products" data-testid="hero-explore-btn"
                                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                                    Explore Products <ArrowRight size={18}/>
                                </Link>
                                <Link to="/contact" data-testid="hero-talk-btn"
                                      className="border-2 border-zinc-200 hover:border-orange-500 text-zinc-900 px-8 py-3.5 rounded-full font-semibold inline-flex items-center justify-center transition-colors">
                                    Book a Demo
                                </Link>
                            </div>
                            <div className="mt-12 flex items-center gap-8 text-sm text-zinc-600">
                                <div>
                                    <div className="font-display font-black text-3xl text-zinc-900">7+</div>
                                    Flagship products
                                </div>
                                <div className="h-10 w-px bg-zinc-200"/>
                                <div>
                                    <div className="font-display font-black text-3xl text-zinc-900">50k+</div>
                                    Active users
                                </div>
                                <div className="h-10 w-px bg-zinc-200 hidden sm:block"/>
                                <div className="hidden sm:block">
                                    <div className="font-display font-black text-3xl text-zinc-900">12+</div>
                                    Countries served
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="grid grid-cols-2 gap-4 fade-in-up">
                                {[
                                    {icon: Rocket, label: "Ship faster", color: "orange"},
                                    {icon: ShieldCheck, label: "Bank-grade security", color: "green"},
                                    {icon: Layers, label: "Modular SaaS", color: "orange"},
                                    {icon: Globe, label: "Globally scalable", color: "green"},
                                ].map((b, i) => (
                                    <div key={i}
                                         className="bg-white border border-zinc-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
                                        <div
                                            className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${b.color === "green" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>
                                            <b.icon size={20}/>
                                        </div>
                                        <div className="font-display font-bold text-zinc-900">{b.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Bento */}
            <section className="py-24 sm:py-32 bg-zinc-50/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Our
                                products
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-black text-zinc-950 tracking-tighter">
                                Seven products. One mission.<br/>
                                <span className="text-orange-500">Run your business better.</span>
                            </h2>
                        </div>
                        <Link to="/products"
                              className="text-orange-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                              data-testid="home-view-all-products">
                            View all <ArrowRight size={16}/>
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        {(Array.isArray(products) ? products : []).map((p, i) => {
                            const layouts = [
                                "md:col-span-7",
                                "md:col-span-5",
                                "md:col-span-5",
                                "md:col-span-7",
                                "md:col-span-4",
                                "md:col-span-4",
                                "md:col-span-4",
                            ];

                            return (
                                <ProductCard
                                    key={p.id}
                                    p={p}
                                    span={layouts[i] || "md:col-span-4"}
                                    accent={p.color || "orange"}
                                />
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-5">
                            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Why
                                Reptile
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950 leading-[1.05]">
                                Built by operators<br/>for operators.
                            </h2>
                            <p className="mt-6 text-zinc-600 text-lg leading-relaxed">
                                Every Reptile product is born from a real operational pain we've solved at scale. We
                                obsess over clean UX, hard reliability and Indian-market realities.
                            </p>
                            <Link to="/about"
                                  className="mt-8 inline-flex items-center gap-2 font-semibold text-orange-600 hover:gap-3 transition-all"
                                  data-testid="home-about-link">
                                Learn our story <ArrowRight size={16}/>
                            </Link>
                        </div>
                        <div className="md:col-span-7 grid grid-cols-2 gap-5">
                            {[
                                {
                                    icon: Zap,
                                    title: "10x faster onboarding",
                                    desc: "Get a team live in days, not months."
                                },
                                {
                                    icon: ShieldCheck,
                                    title: "Compliance ready",
                                    desc: "GST, PF, ESI, e-invoicing — built-in."
                                },
                                {icon: Star, title: "5-star support", desc: "Real engineers on call, not chatbots."},
                                {
                                    icon: Globe,
                                    title: "India-first, world ready",
                                    desc: "Tuned for India, deployed globally."
                                },
                            ].map((b, i) => (
                                <div key={i}
                                     className="bg-white border border-zinc-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-md transition-all">
                                    <div
                                        className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center mb-4">
                                        <b.icon size={20}/>
                                    </div>
                                    <div className="font-display font-bold text-zinc-900 text-lg mb-1">{b.title}</div>
                                    <div className="text-sm text-zinc-600">{b.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-zinc-950 px-8 sm:px-16 py-20">
                        <div className="absolute -top-20 -right-20 w-80 h-80 bg-orange-500/30 rounded-full blur-3xl"/>
                        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-green-500/20 rounded-full blur-3xl"/>
                        <div className="relative text-center max-w-3xl mx-auto">
                            <h3 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white">
                                Ready to ship something serious?
                            </h3>
                            <p className="mt-5 text-zinc-400 text-lg">
                                From product idea to production deployment, we partner end-to-end.
                            </p>
                            <Link to="/contact" data-testid="home-cta-bottom"
                                  className="mt-8 inline-flex bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-full font-semibold transition-colors">
                                Start a project →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
